import { enhancedApiClient, isSuccessResponse, ApiResponse } from './enhancedApiClient';

export interface DispatchProductsRequest {
  productIds: string[];
}

export interface DispatchProductsResponse {
  dispatchedProducts: number;
  orderStatus: string;
}

export interface DispatchOrderResponse {
  dispatchedProducts: number;
  orderStatus: string;
}

export interface OrderStatusSummary {
  orderId: string;
  orderStatus: string;
  totalProducts: number;
  completedProducts: number;
  dispatchedProducts: number;
  pendingProducts: number;
  inProgressProducts: number;
  readyForDispatch: boolean;
  allProductsDispatched: boolean;
  products: Array<{
    id: string;
    name: string;
    status: string;
    type: string;
    totalItems: number;
    completedItems: number;
  }>;
}

// New dispatch flow interfaces
export interface DispatchItem {
  id: string;
  qr_code: string;
  item_type: string;
  quantity: number;
  product_name: string;
  order_number: string;
  customer_name: string;
  status: string;
}

export interface Dispatch {
  id: string;
  vehicleNo: string;
  driverId: string;
  driverName:string;
  driverContact:string;
  supervisorId: string;
  status: string;
  vehicleType:string;
  deliveryOtp?: string;
  deliveryOtpExpiresAt?: string;
  deliveryOtpVerified: boolean;
  dispatchDate: string;
  deliveredAt?: string;
  createdAt: string;
  updatedAt: string;
  remark:string;
  items: DispatchItem[];
  supervisor?: {
    id: string;
    firstName: string;
    lastName: string;
    phone?: string;
    email?: string;
  };
}

export interface CreateDispatchRequest {
  vehicleNo: string;
  vehicleType:string;
  driverId: string;
  driverName?: string;
  driverContact?: string;
  items: string[];
  dispatchDate?: string;
  remarks?: string;
}

export interface UpdateDispatchRequest {
  vehicleNo: string;
  vehicleType:string;
  driverId: string;
  driverName?: string;
  driverContact?: string;
  items: string[];
  dispatchDate?: string;
  remarks?: string;
}

export interface ItemScanResult {
  item: {
    id: string;
    qrCode: string;
    itemNumber: string;
    type: string;
    status: string;
    statusValue: number;
    currentStage: string | null;
    productName: string;
    productType: string;
    orderId: string;
    orderNumber: string;
    customerName: string;
    customerPhone?: string;
    customerId: string;
    quantity: number;
    stageCompletion?: {
      totalStages: number;
      completedStages: number;
      allCompleted: boolean;
    };
  };
  readyForDispatch: boolean;
  message?: string;
}

export interface RequestOTPRequest {
  dispatchId: string;
  customerMobile: string;
}

export interface RequestOTPResponse {
  otp: string;
  expiresAt: string;
  dispatchId: string;
}

export interface VerifyOTPRequest {
  dispatchId: string;
  otp: string;
}

export interface VerifyOTPResponse {
  success: boolean;
  dispatchId: string;
  deliveredItems: number;
  deliveredAt: string;
}

class DispatchService {
  /**
   * Dispatch selected products
   */
  async dispatchProducts(
    orderId: string, 
    productIds: string[]
  ): Promise<ApiResponse<DispatchProductsResponse>> {
    const response = await enhancedApiClient.post(
      `/orders/${orderId}/dispatch-products`,
      { productIds },
      {
        context: "Dispatch Products",
        component: "DispatchService"
      }
    );

    if (isSuccessResponse(response)) {
      return response;
    }

    return response;
  }

  /**
   * Get all dispatch
   */
  async getAllDispatch(): Promise<ApiResponse<Dispatch[]>> {
    const response = await enhancedApiClient.get(
      `/dispatch`,
      {
        context: "Get All Dispatches",
        component: "DispatchService"
      }
    );

    if (isSuccessResponse(response)) {
      return response;
    }

    return response;
  }

  /**
   * Dispatch complete order
   */
  async dispatchOrder(orderId: string): Promise<ApiResponse<DispatchOrderResponse>> {
    const response = await enhancedApiClient.post(
      `/orders/${orderId}/dispatch-order`,
      {},
      {
        context: "Dispatch Order",
        component: "DispatchService"
      }
    );

    if (isSuccessResponse(response)) {
      return response;
    }

    return response;
  }

  /**
   * Get order status summary
   */
  async getOrderStatusSummary(orderId: string): Promise<ApiResponse<OrderStatusSummary>> {
    const response = await enhancedApiClient.get(
      `/orders/${orderId}/status-summary`,
      {
        context: "Get Order Status Summary",
        component: "DispatchService"
      }
    );

    if (isSuccessResponse(response)) {
      return response;
    }

    return response;
  }

  /**
   * Get item by QR code or item ID for dispatch scanning
   */
  async getItemByCode(code: string): Promise<ApiResponse<ItemScanResult>> {
    const response = await enhancedApiClient.get(
      `/dispatch/items/${code}`,
      {
        context: "Get Item by Code",
        component: "DispatchService"
      }
    );

    if (isSuccessResponse(response)) {
      return response;
    }

    return response;
  }

  /**
   * Create a new dispatch
   */
  async createDispatch(payload: CreateDispatchRequest): Promise<ApiResponse<Dispatch>> {
    const response = await enhancedApiClient.post(
      `/dispatch/create`,
      payload,
      {
        context: "Create Dispatch",
        component: "DispatchService"
      }
    );

    if (isSuccessResponse(response)) {
      return response;
    }

    return response;
  }

  /**
   * CUpdate dispatch
   */
  async updateDispatch(Id: string,payload: UpdateDispatchRequest): Promise<ApiResponse<Dispatch>> {
    const response = await enhancedApiClient.put(
      `/dispatch/${Id}`,
      payload,
      {
        context: "Update Dispatch",
        component: "DispatchService"
      }
    );

    if (isSuccessResponse(response)) {
      return response;
    }

    return response;
  }

  /**
   * Get dispatch details
   */
  async getDispatchDetails(dispatchId: string): Promise<ApiResponse<Dispatch>> {
    const response = await enhancedApiClient.get(
      `/dispatch/${dispatchId}`,
      {
        context: "Get Dispatch Details",
        component: "DispatchService"
      }
    );

    if (isSuccessResponse(response)) {
      return response;
    }

    return response;
  }

  /**
   * Get dispatches assigned to a driver
   */
  async getDriverDispatches(driverId: string): Promise<ApiResponse<Dispatch[]>> {
    const response = await enhancedApiClient.get(
      `/dispatch/driver/${driverId}`,
      {
        context: "Get Driver Dispatches",
        component: "DispatchService"
      }
    );

    if (isSuccessResponse(response)) {
      return response;
    }

    return response;
  }

  /**
   * Request OTP for delivery
   */
  async requestOTP(payload: RequestOTPRequest): Promise<ApiResponse<RequestOTPResponse>> {
    const response = await enhancedApiClient.post(
      `/dispatch/request-otp`,
      payload,
      {
        context: "Request OTP",
        component: "DispatchService"
      }
    );

    if (isSuccessResponse(response)) {
      return response;
    }

    return response;
  }

  /**
   * Verify OTP and mark items as delivered
   */
  async verifyOTP(payload: VerifyOTPRequest): Promise<ApiResponse<VerifyOTPResponse>> {
    const response = await enhancedApiClient.post(
      `/dispatch/verify-otp`,
      payload,
      {
        context: "Verify OTP",
        component: "DispatchService"
      }
    );

    if (isSuccessResponse(response)) {
      return response;
    }

    return response;
  }

  /**
   * Get dispatch logs
   */
  async getDispatchLogs(dispatchId: string): Promise<ApiResponse<any[]>> {
    const response = await enhancedApiClient.get(
      `/dispatch/${dispatchId}/logs`,
      {
        context: "Get Dispatch Logs",
        component: "DispatchService"
      }
    );

    if (isSuccessResponse(response)) {
      return response;
    }

    return response;
  }
}

export const dispatchService = new DispatchService();
